import { ResourceContext } from 'app/context/resource';
import { IResourceItem } from 'app/context/resource/types';
import { pickResource } from 'app/definitions/helpers/pick-resource';
import { useContext } from 'react';

export type UseResource = {
  resourceNameOrRouteName: string;
};

/**
 * Matches the resource by identifier.
 * If not provided, the resource from the route will be returned.
 * If your resource does not explicitly define an identifier, the resource name will be used.
 */
export type UseResourceParam = string | undefined;

type SelectReturnType<T extends boolean> = T extends true
  ? { resource: IResourceItem; identifier: string }
  : { resource: IResourceItem; identifier: string } | undefined;

export type UseResourceReturnType = {
  resources: IResourceItem[];
  resource?: IResourceItem;

  select: <T extends boolean = true>(
    resourceName: string,
    force?: T
  ) => SelectReturnType<T>;
  identifier?: string;
};

type UseResourceReturnTypeWithResource = UseResourceReturnType & {
  resource: IResourceItem;
  identifier: string;
};
export function useResource(): UseResourceReturnType;
export function useResource<TIdentifier = UseResourceParam>(
  identifier: TIdentifier
): TIdentifier extends NonNullable<UseResourceParam>
  ? UseResourceReturnTypeWithResource
  : UseResourceReturnType;

export function useResource(args?: UseResourceParam): UseResourceReturnType {
  const { resources } = useContext(ResourceContext);

  const select = <T extends boolean = true>(
    resourceName: string,
    force = true
  ): SelectReturnType<T> => {
    const pickedResource = pickResource(resourceName, resources);

    if (pickedResource) {
      return {
        resource: pickedResource,
        identifier: pickedResource.identifier ?? pickedResource.name
      } as SelectReturnType<T>;
    }

    if (force) {
      const resource: IResourceItem = {
        name: resourceName,
        identifier: resourceName
      };

      const identifier = resource.identifier ?? resource.name;

      return {
        resource,
        identifier
      } as SelectReturnType<T>;
    }

    return undefined as SelectReturnType<T>;
  };

  /** New Router */
  let resource: IResourceItem | undefined = undefined;
  // we try to pick the resource from props first
  const identifier = typeof args === 'string' ? args : '';
  if (identifier) {
    const pickedFromProps = pickResource(identifier, resources);
    if (pickedFromProps) {
      resource = pickedFromProps;
    } else {
      resource = {
        name: identifier as string
      };
    }
  }

  return {
    resources,
    resource,
    select,
    identifier: resource?.identifier ?? resource?.name
  };
}
